import { computed } from '@angular/core';

import {
  patchState,
  signalStore,
  withComputed,
  withHooks,
  withMethods,
  withState,
} from '@ngrx/signals';
import { Default_Login_Page } from './default_login_page.constant';
import {
  ChatMessage,
  ChatMessageTypes,
  ComposedPage,
  CrossFrameMessage,
  CrossFrameMessageTypes,
} from '@hyper-inferno/survey-elements-core-utils';
import {
  ComposedSurveyPlugin,
  ResponseRecord,
} from '@hyper-inferno/survey-elements-core';

export enum RenderStates {
  //   QUESTION = 'question',

  INITIALIZING = 'initializing',
  LOGIN = 'login',
  PAGE = 'page',

  LOADING = 'loading',
  ERROR_OCCURED = 'error',
  EXPIRED = 'EXPIRED',
  COMPLETED = 'completed',
  END = 'end',
}

export interface RenderStatePayload {
  errorStatePayload?: {
    errorMessage: string;
  };
  endStatePayload?: {
    endMessage: string;
  };
  loadingPayload?: {
    watcher: any;
    globalTheme: {
      globalHeader: string;
      globalFooter: string;
    };
  };

  startConsentPayload?: {
    loginPage: string;
  };
}

export type QuestionState = {
  data: ResponseRecord; // The coerced ResponseRecord
  isValid: boolean;
  error: string | null;
  validate: () => boolean;
};

export type PluginState = {
  data: ResponseRecord;
  isValid: boolean;
  error: string | null;
  validate: () => boolean;
  onBeforeSubmit: () => void;
};

type APPState = {
  //   currentQuestionState: {
  //     question?: ComposedQuestion;
  //   };

  currentPageState: {
    page?: ComposedPage;
  };

  currentResponseState: {
    isValid: boolean;
    answerPayload: {
      answerObject: ResponseRecord;
    };
  };
  renderState: RenderStates;
  renderPayload: RenderStatePayload;
  pendingResolvers: Record<string, (value: any) => void>;
  registry: Record<string, QuestionState>;
  pluginRegistry: Record<string, PluginState>;
  submitAttempted: number;
  oe_mappings: Record<string, string>;
  globalPlugins: ComposedSurveyPlugin[];
  survey_language: string;
};

const initialState: APPState = {
  currentPageState: {
    page: undefined,
  },

  //   questionRenderDefinitions: [],
  currentResponseState: {
    answerPayload: {
      answerObject: {},
    },
    isValid: false,
  },
  renderState: RenderStates.INITIALIZING,
  renderPayload: {},
  pendingResolvers: {},
  registry: {} as Record<string, QuestionState>,
  pluginRegistry: {} as Record<string, PluginState>,
  submitAttempted: 0,
  oe_mappings: {},
  globalPlugins: [],
  survey_language: '',
};

export const SurveyRenderControllerStore = signalStore(
  { providedIn: 'root', protectedState: false },
  withState(initialState),
  withComputed((store) => ({
    isPageValid: computed(() => {
      const states = Object.values(store.registry());
      return states.every((s) => s.isValid);
    }),
    // Flatten all data objects into the final payload for the Bot
    pageData: computed(() => {
      const registry = store.registry();
      return Object.entries(registry).reduce((acc, [key, value]) => {
        return {
          ...acc,
          [key]: { ...value.data },
        };
      }, {});
    }),
  })),

  withMethods((store) => ({
    receiveMessage(event: CrossFrameMessage): void {
      if (typeof event == 'string') {
        event = JSON.parse(event);
      }
      switch (event.type) {
        case CrossFrameMessageTypes.CONTROLLER_MESSAGE:
          if (event.chatMessage) this.handleChatMessage(event.chatMessage);
          break;
        case CrossFrameMessageTypes.CONTROLLER_RELOAD_COMMAND:
          location.reload();
          break;
        default:
          // console.log('Unhandled message from Controller', event);
          break;
      }
    },
    handleChatMessage(chatMessage: ChatMessage) {
      switch (chatMessage.messageType) {
        // case ChatMessageTypes.Question:
        //   this.setupCurrentQuestion(chatMessage.question);
        //   break;
        case ChatMessageTypes.Page:
          this.setupCurrentPage(chatMessage.page);
          break;
        case ChatMessageTypes.StartConsent:
          this.setRenderState(RenderStates.LOGIN, {
            startConsentPayload: {
              loginPage: chatMessage.message ?? Default_Login_Page,
            },
          });
          break;
        case ChatMessageTypes.EndSurvey:
          // this.setupCurrentPage(chatMessage.page);
          this.setRenderState(RenderStates.END, {
            endStatePayload: {
              endMessage: chatMessage.message ?? 'Thank you for your time',
            },
          });
          break;
        case ChatMessageTypes.BOT_ERROR:
          this.setRenderState(RenderStates.ERROR_OCCURED, {
            errorStatePayload: {
              errorMessage: chatMessage.message ?? 'An Error has occured',
            },
          });
          break;

        case ChatMessageTypes.UNKNOWN:
          this.setRenderState(RenderStates.END, {
            endStatePayload: {
              endMessage: chatMessage.message ?? 'Unhandled Operation',
            },
          });
          break;
        default:
          break;
      }
    },

    setupCurrentPage(page: ComposedPage | undefined) {
      patchState(store, (state) => ({
        currentPageState: { page },
        registry: {},
        submitAttempted: 0,
        oe_mappings: {},
        pluginRegistry: {},
      }));
      this.setRenderState(RenderStates.PAGE);
    },
    // setCurrentAnswerState(answer: {
    //   isValid: boolean;
    //   answerPayload: {
    //     answerObject: ResponseRecord;
    //     latlng?: any;
    //     pageTime?: any;
    //   };
    // }) {
    //   // Run schema Validation
    //   try {
    //     const coercedPayload = this.coerecedPayloadBuilders(
    //       answer.answerPayload.answerObject,
    //       store.currentQuestionState()?.question,
    //     );

    //     patchState(store, (state) => ({
    //       currentResponseState: {
    //         answerPayload: { answerObject: coercedPayload },
    //         isValid: answer.isValid,
    //       },
    //     }));

    //     this.updateSubmitedAsnwersState(coercedPayload);
    //   } catch (e) {
    //     throw new Error((e as Error)?.message);
    //   }
    // },
    setRenderState(
      renderState: RenderStates,
      renderPayload: RenderStatePayload = {},
    ) {
      patchState(store, (state) => ({
        renderState,
        renderPayload,
      }));
    },
    // coerecedPayloadBuilders(
    //   answerPayload: ResponseRecord,
    //   page: ComposedQuestion | any,
    // ) {
    //   return coerecedPayloadBuilder(answerPayload, question);
    //   // const ajvSchema = buildAjvSchema(questionMeta ?? {});
    //   // const ajv = new Ajv({ coerceTypes: true, useDefaults: true }); // Enable type coercion
    //   // // Compile the schema
    //   // const validate = ajv.compile(ajvSchema);
    //   // // Serialize form data (using jQuery)
    //   // const answerObjectSnap = { ...answerPayload };
    //   // // Validate and coerce the data
    //   // const valid = validate(answerObjectSnap);
    //   // if (valid) return answerObjectSnap as ResponseRecord;
    //   // else {
    //   //   throw new Error(
    //   //     `Validation Errors: \n ${printAjvErrors(validate.errors ?? [])}`
    //   //   );
    //   // }
    // },
    updateQuestionState(
      id: string,
      data: any,
      isValid: boolean,
      error: string | null = null,
      validate: () => boolean,
    ) {
      patchState(store, (state) => ({
        registry: {
          ...state.registry,
          [id]: { data, isValid, error, validate },
        },
      }));
    },
    attemptSubmit() {
      patchState(store, { submitAttempted: store.submitAttempted() + 1 });
    },
    updateOEMapping(optionId: string, referenceName: string, value: string) {
      const current = store.oe_mappings();

      if (value && value.trim().length > 0) {
        // Add or update the reference name for this option
        patchState(store, {
          oe_mappings: { ...current, [optionId]: referenceName },
        });
      } else {
        // Remove it if empty to keep the store clean
        const { [optionId]: removed, ...rest } = current;
        patchState(store, { oe_mappings: rest });
      }
    },

    setGlobalPlugins(globalPlugins: ComposedSurveyPlugin[]) {
      patchState(store, { globalPlugins });
    },
    updatePluginState(
      id: string,
      data: any,
      isValid: boolean,
      error: string | null = null,
      validate: () => boolean,
      onBeforeSubmit: () => void,
    ) {
      patchState(store, (state) => ({
        pluginRegistry: {
          ...state.pluginRegistry,
          [id]: { data, isValid, error, validate, onBeforeSubmit },
        },
      }));
    },
    clearPlugin(id: string) {
      patchState(store, (state) => {
        // Use destructuring to pull out the target ID, and gather the rest
        const { [id]: _, ...remainingPlugins } = state.pluginRegistry;

        return {
          pluginRegistry: remainingPlugins,
        };
      });
    },
    setLanguage(lang_code: string) {
      patchState(store, (state) => ({
        survey_language: lang_code,
      }));
    },
    clearStore() {
      patchState(store, initialState)
    }
  })),
  withHooks({
    onInit(store) { },
    onDestroy(store) { },
  }),
);
